import { useEffect, type RefObject } from "react";
import { trackOnce } from "../utils/tracking";

type YTPlayer = {
  getCurrentTime(): number;
  getDuration(): number;
  getPlayerState(): number;
};

type YTPlayerOptions = {
  host?: string;
  events: {
    onReady?: (e: { target: YTPlayer }) => void;
    onStateChange?: (e: { data: number; target: YTPlayer }) => void;
  };
};

type YTNamespace = {
  Player: new (el: HTMLIFrameElement, opts: YTPlayerOptions) => YTPlayer;
};

declare global {
  interface Window {
    YT?: YTNamespace;
    onYouTubeIframeAPIReady?: () => void;
  }
}

const STATE_ENDED = 0;
const STATE_PLAYING = 1;

let apiPromise: Promise<YTNamespace> | null = null;

function loadYouTubeApi(): Promise<YTNamespace> {
  if (window.YT?.Player) return Promise.resolve(window.YT);
  if (apiPromise) return apiPromise;
  apiPromise = new Promise((resolve) => {
    const previous = window.onYouTubeIframeAPIReady;
    window.onYouTubeIframeAPIReady = () => {
      previous?.();
      if (window.YT) resolve(window.YT);
    };
    const script = document.createElement("script");
    script.src = "https://www.youtube.com/iframe_api";
    script.async = true;
    document.head.appendChild(script);
  });
  return apiPromise;
}

/** Fires VideoPlay / Video50 / VideoComplete (once per page load) for an embedded YouTube iframe. */
export function useYouTubeTracking(iframeRef: RefObject<HTMLIFrameElement>, enabled: boolean): void {
  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;
    let poll: number | null = null;

    const stopPoll = () => {
      if (poll !== null) window.clearInterval(poll);
      poll = null;
    };
    const startPoll = (player: YTPlayer) => {
      if (poll !== null) return;
      poll = window.setInterval(() => {
        try {
          const duration = player.getDuration();
          if (duration > 0 && player.getCurrentTime() / duration >= 0.5) {
            trackOnce("Video50", {}, { custom: true });
          }
        } catch {
          stopPoll();
        }
      }, 1000);
    };
    const onPlaying = (player: YTPlayer) => {
      trackOnce("VideoPlay", {}, { custom: true });
      startPoll(player);
    };

    loadYouTubeApi()
      .then((YT) => {
        const iframe = iframeRef.current;
        if (cancelled || !iframe) return;
        new YT.Player(iframe, {
          host: "https://www.youtube-nocookie.com",
          events: {
            onReady: (e) => {
              if (e.target.getPlayerState() === STATE_PLAYING) onPlaying(e.target);
            },
            onStateChange: (e) => {
              if (e.data === STATE_PLAYING) {
                onPlaying(e.target);
                return;
              }
              stopPoll();
              if (e.data === STATE_ENDED) {
                trackOnce("Video50", {}, { custom: true });
                trackOnce("VideoComplete", {}, { custom: true });
              }
            },
          },
        });
      })
      .catch(() => undefined);

    return () => {
      cancelled = true;
      stopPoll();
    };
  }, [enabled, iframeRef]);
}
